import type { DataViewFilter } from 'components/DataView';
import { anyStateOrganizationSource, internalUserSource, siteSource } from 'data/entity-sources/sources';

import { DELIVERY_STATES_LABELS } from './states';
import type { DeliveryRow } from './deliveryViews';

const STATE_OPTIONS = Object.entries(DELIVERY_STATES_LABELS).map(([value, label]) => ({ value, label }));

/**
 * `accessor` présent ⇒ la `DataView` filtre les lignes déjà chargées.
 * `accessor` absent  ⇒ filtre SERVEUR : la valeur est remontée par `onFiltersChange` et devient une
 *                      variable de la query. C'est le cas du périmètre « En cours », du site et de la
 *                      plage de dates, qui changent l'ensemble chargé plutôt que de le restreindre.
 */
export function buildDeliveryFilters(roundOptions: { value: string; label: string }[]): DataViewFilter<DeliveryRow>[] {
    return [
        {
            // Le périmètre de travail, allumé par défaut, comme sur les commandes : les livraisons en
            // cours plus celles archivées du jour. Sans lui la page lirait toute la collection.
            key: 'isCurrent',
            title: 'En cours',
            type: 'boolean',
            defaultValue: true,
        },
        {
            key: 'state',
            title: 'État',
            type: 'multi-select',
            options: STATE_OPTIONS,
            accessor: (delivery) => delivery.state,
        },
        {
            key: 'organizationId',
            title: 'Organisation',
            type: 'async-multi-select',
            source: anyStateOrganizationSource,
            accessor: (delivery) => delivery.organizationId,
        },
        {
            // Le filtre Site part au SERVEUR : sans plage de dates, le chemin par défaut ne rend que les
            // livraisons non archivées (~90 sur 80 000), c'est-à-dire presque rien sur un site donné — un
            // lien « voir toutes les livraisons de ce site » afficherait moins que la section qui y mène.
            key: 'siteId',
            title: 'Site',
            type: 'async-multi-select',
            source: siteSource,
        },
        {
            // 'true' | 'false' (`Delivery.isDynamic` stringifié) ; rien de coché = les deux flux.
            key: 'isDynamic',
            title: 'Type de livraison',
            type: 'multi-select',
            options: [
                { value: 'false', label: 'Fixe' },
                { value: 'true', label: 'Dynamique' },
            ],
            accessor: (delivery) => String(delivery.isDynamic),
        },
        {
            key: 'deliveryPersonUserId',
            title: 'Livreur',
            type: 'async-multi-select',
            source: internalUserSource,
            accessor: (delivery) => delivery.deliveryPersonUserId,
        },
        {
            key: 'roundName',
            title: 'Tournée',
            type: 'multi-select',
            options: roundOptions,
            accessor: (delivery) => delivery.roundName?.trim(),
        },
        {
            key: 'deliveryDateRange',
            title: 'Date de livraison',
            type: 'date-range',
        },
    ];
}
